import { Alert } from "@hakistudio/hakiui";
import { useEffect, useState } from "react";
import { SebasLogo } from "@/components/brand/SebasLogo";
import { Button } from "@/components/ui/Button";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { SIGNED_IN_WITH_GOOGLE } from "@/components/GoogleCalendarAutoConnect";
import { authClient, signInWithGoogleViaSystemBrowser } from "@/lib/auth";
import { isTauriMobile } from "@/lib/platform";
import { Input } from "@/components/ui/Field";

type Mode = "sign-in" | "sign-up";

/** Extracts a human-readable message from Better Auth's varied error shapes. */
function messageFrom(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const e = error as { message?: string; error?: { message?: string }; statusText?: string };
    return e.message ?? e.error?.message ?? e.statusText ?? "Something went wrong";
  }
  return "Something went wrong";
}

export function SignInPage() {
  const [mode, setMode] = useState<Mode>("sign-in");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Surfaces an OAuth failure that happened off-site.
   *
   * A provider that refuses the handshake sends the user back here with the
   * reason in the query string — nothing throws in this tab, so without reading
   * it the screen just re-renders as an empty form and the failure looks like a
   * page that did nothing. The params are stripped afterwards so a reload does
   * not resurrect a stale error.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("error_description") ?? params.get("error");
    if (!reason) return;

    setError(reason.replace(/[_+]/g, " "));
    params.delete("error");
    params.delete("error_description");
    const query = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (query ? `?${query}` : ""));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res =
        mode === "sign-up"
          ? await authClient.signUp.email({ email, password, name: name || email })
          : await authClient.signIn.email({ email, password });
      if (res?.error) setError(messageFrom(res.error));
      // On success the session cookie is set and useSession() re-renders the app.
    } catch (err) {
      setError(messageFrom(err));
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setError(null);
    setBusy(true);
    // Read once the app loads, to offer the Calendar consent straight away.
    // Session-scoped so it never leaks into a later email/password sign-in.
    try {
      sessionStorage.setItem(SIGNED_IN_WITH_GOOGLE, "1");
    } catch {
      // Storage is unavailable; the Settings card still covers connecting.
    }
    try {
      // Mobile webviews can't run Google's OAuth (it refuses embedded user
      // agents) and can't receive a redirect to the app's internal origin. Run
      // the handshake in the system browser and come back via the deep link;
      // `signInWithGoogleViaSystemBrowser` navigates on success.
      if (isTauriMobile()) {
        await signInWithGoogleViaSystemBrowser();
        return;
      }

      const res = await authClient.signIn.social({
        provider: "google",
        callbackURL: window.location.origin,
      });
      // Better Auth reports a refused handshake in the resolved value, not by
      // throwing. Only checking `catch` here left the button looking inert on
      // every provider-side failure.
      if (res?.error) setError(messageFrom(res.error));
    } catch (err) {
      setError(messageFrom(err));
    } finally {
      // A successful call navigates away, so this only runs when we stayed put.
      setBusy(false);
    }
  };

  return (
    // Two rows: a full-bleed header so the logo and toggle land in the real page
    // corners at every width, and a scroll region that owns the vertical rhythm.
    <div className="flex min-h-dvh flex-col bg-surface text-ink">
      <header className="flex shrink-0 items-center justify-between px-5 py-5 pt-safe sm:px-8 sm:py-6">
        <SebasLogo />
        <ThemeToggle />
      </header>

      {/*
        `m-auto` centres the card when there is spare height and collapses to a
        top-weighted flow when the form is taller than the viewport (sign-up on a
        short screen, or the keyboard open) — the page then scrolls instead of
        clipping the heading, which `items-center` would do.
      */}
      <main className="flex flex-1 flex-col px-5 pb-16 pt-2 sm:pb-24 sm:pt-8">
        {/* `m-auto` collapses to a top margin of 0 when the card outgrows the
            viewport (sign-up on a short window); `pt` on the parent then keeps a
            little breathing room above it and the page scrolls for the rest. */}
        <div className="m-auto w-full max-w-[25rem]">
          {/*
            Borderless column on phones — matching the app's edge-to-edge mobile
            chrome — and a contained card from `sm` up so the form has presence
            on a wide screen instead of floating in empty space.
          */}
          <div className="flex flex-col sm:rounded-card sm:border sm:border-line sm:bg-raised sm:p-9 sm:shadow-card">
            <h1 className="font-display text-4xl font-semibold sm:text-5xl">
              {mode === "sign-in" ? (
                <>
                  Welcome
                  <br />
                  back
                </>
              ) : (
                <>
                  Start
                  <br />
                  writing
                </>
              )}
            </h1>
            <p className="mt-3 text-sm text-ink-muted">
              {mode === "sign-in"
                ? "Sign in to reach your notes and schedule."
                : "Create an account to keep your notes on every device."}
            </p>

            {/*
              `lg` controls (40px, 16px text) — this is a standalone focal screen,
              not a dense app view, so the fields and the primary action carry the
              weight HakiUI's own "Large" size is drawn for. The rest of the app
              stays on the default `md`.
            */}
            <form onSubmit={submit} className="mt-8 flex flex-col gap-3">
              {mode === "sign-up" ? (
                <Input
                  size="lg"
                  placeholder="Your name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              ) : null}

              <Input
                size="lg"
                type="email"
                required
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                size="lg"
                type="password"
                required
                minLength={8}
                placeholder="Password"
                autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {error ? <Alert variant="danger" title={error} /> : null}

              <Button type="submit" size="lg" disabled={busy} className="mt-1 w-full">
                {busy ? "One moment…" : mode === "sign-in" ? "Sign in" : "Create account"}
              </Button>
            </form>

            <div className="my-5 flex items-center gap-3 text-xs text-ink-faint">
              <span className="h-px flex-1 bg-line" />
              or
              <span className="h-px flex-1 bg-line" />
            </div>

            {/*
              Both sit outside the <form>, but a bare <button> defaults to
              type="submit" — being explicit keeps them inert if either is ever
              moved inside it.
            */}
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={google}
              disabled={busy}
              className="w-full"
            >
              Continue with Google
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setMode(mode === "sign-in" ? "sign-up" : "sign-in");
                setError(null);
              }}
              // HakiUI writes `color` as an inline style on ghost, and inline beats a
              // utility class — so the muted tone has to come through `style` too.
              style={{ color: "var(--sb-ink-muted)" }}
              className="mt-5 w-full"
            >
              {mode === "sign-in" ? "No account yet? Create one" : "Already have an account? Sign in"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
